import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import {
  DataQualityStats,
  EMPTY_PAYLOAD,
  EventsPer50Km,
  RiskAverages,
  RiskByStatus,
  SafetyDashboardPayload,
  SafetyRootResponse,
  SafetySummary,
  SpeedStatistics,
  StatusCount,
  TopRiskVehicle,
  TopSpeedVehicle,
  VehicleSafetyDetail,
} from './safety-dashboard.model';

@Injectable({ providedIn: 'root' })
export class SafetyDashboardStore {
  private readonly _payload$ = new BehaviorSubject<SafetyDashboardPayload>(EMPTY_PAYLOAD);
  private readonly _loading$ = new BehaviorSubject<boolean>(false);

  readonly payload$: Observable<SafetyDashboardPayload> = this._payload$.asObservable();
  readonly loading$: Observable<boolean> = this._loading$.asObservable();

  readonly summary$: Observable<SafetySummary | null> = this._payload$.pipe(
    map((p) => p.summary),
    distinctUntilChanged()
  );
  readonly statusDistribution$: Observable<StatusCount[]> = this._payload$.pipe(
    map((p) => p.statusDistribution),
    distinctUntilChanged()
  );
  readonly topRiskVehicles$: Observable<TopRiskVehicle[]> = this._payload$.pipe(
    map((p) => p.topRiskVehicles),
    distinctUntilChanged()
  );
  readonly riskAverages$: Observable<RiskAverages | null> = this._payload$.pipe(
    map((p) => p.riskAverages),
    distinctUntilChanged()
  );
  readonly speedStatistics$: Observable<SpeedStatistics | null> = this._payload$.pipe(
    map((p) => p.speedStatistics),
    distinctUntilChanged()
  );
  readonly topSpeedVehicles$: Observable<TopSpeedVehicle[]> = this._payload$.pipe(
    map((p) => p.topSpeedVehicles),
    distinctUntilChanged()
  );
  readonly riskByStatus$: Observable<RiskByStatus[]> = this._payload$.pipe(
    map((p) => p.riskByStatus),
    distinctUntilChanged()
  );
  readonly eventsPer50Km$: Observable<EventsPer50Km | null> = this._payload$.pipe(
    map((p) => p.eventsPer50Km),
    distinctUntilChanged()
  );
  readonly dataQuality$: Observable<DataQualityStats | null> = this._payload$.pipe(
    map((p) => p.dataQuality),
    distinctUntilChanged()
  );
  readonly vehicles$: Observable<VehicleSafetyDetail[]> = this._payload$.pipe(
    map((p) => p.vehicles),
    distinctUntilChanged()
  );

  readonly totalVehicles$: Observable<number> = this._payload$.pipe(
    map((p) => p.summary?.TotalVehicles ?? p.vehicles.length ?? 0),
    distinctUntilChanged()
  );

  get snapshot(): SafetyDashboardPayload {
    return this._payload$.value;
  }

  get loadingSnapshot(): boolean {
    return this._loading$.value;
  }

  setLoading(v: boolean): void {
    if (this._loading$.value !== v) 
      this._loading$.next(v);
  }

  clear(): void {
    this._payload$.next(EMPTY_PAYLOAD);
  }

  setPayload(raw: SafetyRootResponse | null | undefined): void {
    if (!raw) {
      this._payload$.next(EMPTY_PAYLOAD);
      return;
    }

    this._payload$.next({
      summary: Array.isArray(raw.Table) && raw.Table.length ? raw.Table[0] : null,
      statusDistribution: Array.isArray(raw.Table1) ? raw.Table1 : [],
      topRiskVehicles: Array.isArray(raw.Table2) ? raw.Table2 : [],
      riskAverages: Array.isArray(raw.Table3) && raw.Table3.length ? raw.Table3[0] : null,
      speedStatistics: Array.isArray(raw.Table4) && raw.Table4.length ? raw.Table4[0] : null,
      topSpeedVehicles: Array.isArray(raw.Table5) ? raw.Table5 : [],
      riskByStatus: [],
      eventsPer50Km: Array.isArray(raw.Table6) && raw.Table6.length ? raw.Table6[0] : null,
      dataQuality: Array.isArray(raw.Table7) && raw.Table7.length ? raw.Table7[0] : null,
      vehicles: Array.isArray(raw.Table8) ? raw.Table8 : [],
    });
  }
  
}
